import { useEffect, useId, useRef, useState } from 'react'

import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineInput } from '@/components/ui/underline-input'
import { loginTitle } from '@/lib/branding'

export interface SettingsBranding {
  name: string
  logoUrl?: string
  /** Raw stored value — undefined means "none uploaded" (the app falls back to logoUrl). */
  logoOnLightUrl?: string
}

export interface SettingsBrandingValues {
  name: string
  logoFile?: File | null
  removeLogo?: boolean
  logoOnLightFile?: File | null
  removeLogoOnLight?: boolean
}

export interface SettingsTwilio {
  accountSid: string
  fromNumber: string
  messagingServiceSid: string
  /** The token itself never reaches the app — only whether one is stored. */
  hasAuthToken: boolean
}

export interface SettingsTwilioValues {
  accountSid: string
  fromNumber: string
  messagingServiceSid: string
  /** Empty/undefined leaves the stored token untouched. */
  authToken?: string
  clearAuthToken?: boolean
}

export interface SettingsScreenProps {
  branding: SettingsBranding
  onSaveBranding?: (values: SettingsBrandingValues) => Promise<void> | void
  isSaving?: boolean
  errorMessage?: string | null
  successMessage?: string | null
  /** Twilio connection; omit while loading, null when it could not be loaded. */
  twilio?: SettingsTwilio | null
  onSaveTwilio?: (values: SettingsTwilioValues) => Promise<void> | void
  isSavingTwilio?: boolean
  twilioErrorMessage?: string | null
  twilioSuccessMessage?: string | null
  /** Admin curation of the follow-up category list (smart component slot). */
  followUpCategoriesSection?: React.ReactNode
  /** The master sending switch (a smart component slot; also on the Reminders page). */
  whatsappSendingSwitch?: React.ReactNode
  /** Queued-message cancel window (seconds); omit while loading. */
  whatsappSendDelaySeconds?: number
  onSaveSendDelay?: (seconds: number) => Promise<void> | void
  isSavingSendDelay?: boolean
  sendDelayErrorMessage?: string | null
  sendDelaySuccessMessage?: string | null
}

const LOGO_ACCEPT = 'image/svg+xml,image/png,image/webp,image/jpeg'
const LOGO_MAX_BYTES = 1048576

type LogoDraft = { file: File | null; remove: boolean; previewUrl: string | null }
const emptyDraft: LogoDraft = { file: null, remove: false, previewUrl: null }

function useObjectUrl(file: File | null) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!file) {
      setUrl(null)
      return
    }
    const next = URL.createObjectURL(file)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [file])
  return url
}

function LogoField(props: {
  id: string
  label: string
  description: string
  tone: 'dark' | 'light'
  currentUrl?: string
  draft: LogoDraft
  onChange: (draft: LogoDraft) => void
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const previewUrl = useObjectUrl(props.draft.file)
  const shownUrl = props.draft.remove ? null : (previewUrl ?? props.currentUrl ?? null)
  const dirty = Boolean(props.draft.file) || props.draft.remove
  const frame =
    props.tone === 'dark'
      ? 'bg-slate-900 text-slate-400'
      : 'bg-white text-slate-400 border border-slate-200'

  const pick: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0] ?? null
    event.target.value = ''
    if (!file) return
    if (file.size > LOGO_MAX_BYTES) {
      setFileError('That file is over 1 MB. Please pick a smaller image.')
      return
    }
    setFileError(null)
    props.onChange({ file, remove: false, previewUrl: null })
  }

  return (
    <div className="space-y-2">
      <FormFieldLabel htmlFor={props.id}>{props.label}</FormFieldLabel>
      <p className="text-[13px] text-slate-500">{props.description}</p>
      <div className={`flex h-28 items-center justify-center rounded-lg px-6 ${frame}`}>
        {shownUrl ? (
          <img src={shownUrl} alt="" className="max-h-20 w-auto max-w-full" />
        ) : (
          <span className="text-sm">No logo — the shop name is shown instead</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          id={props.id}
          type="file"
          accept={LOGO_ACCEPT}
          className="hidden"
          onChange={pick}
          disabled={props.disabled}
        />
        <button
          type="button"
          disabled={props.disabled}
          onClick={() => inputRef.current?.click()}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {shownUrl ? 'Replace image' : 'Choose image'}
        </button>
        {shownUrl && !props.draft.file ? (
          <button
            type="button"
            disabled={props.disabled}
            onClick={() => props.onChange({ file: null, remove: true, previewUrl: null })}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
          >
            Remove
          </button>
        ) : null}
        {dirty ? (
          <button
            type="button"
            disabled={props.disabled}
            onClick={() => {
              setFileError(null)
              props.onChange(emptyDraft)
            }}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            Undo
          </button>
        ) : null}
        {props.draft.file ? (
          <span className="text-xs text-slate-500">{props.draft.file.name}</span>
        ) : props.draft.remove ? (
          <span className="text-xs text-slate-500">Will be removed on save</span>
        ) : null}
      </div>
      {fileError ? <p className="text-[13px] text-rose-600">{fileError}</p> : null}
    </div>
  )
}

export function SettingsScreen(props: SettingsScreenProps) {
  const { branding, onSaveBranding, isSaving = false, errorMessage, successMessage } = props
  const idPrefix = useId()
  const nameId = `${idPrefix}-name`
  const [name, setName] = useState(branding.name)
  const [nameTouched, setNameTouched] = useState(false)
  const [logoDraft, setLogoDraft] = useState<LogoDraft>(emptyDraft)
  const [lightDraft, setLightDraft] = useState<LogoDraft>(emptyDraft)

  // Follow server-side changes (another admin, realtime) until the field is edited.
  useEffect(() => {
    if (!nameTouched) setName(branding.name)
  }, [branding.name, nameTouched])

  const trimmedName = name.trim()
  const nameChanged = trimmedName !== branding.name
  const dirty =
    nameChanged || Boolean(logoDraft.file) || logoDraft.remove || Boolean(lightDraft.file) || lightDraft.remove
  const canSave = dirty && trimmedName.length > 0 && !isSaving

  const previewLogo = useObjectUrl(logoDraft.file)
  const previewLight = useObjectUrl(lightDraft.file)
  const effectiveLogo = logoDraft.remove ? undefined : (previewLogo ?? branding.logoUrl)
  const effectiveLight = lightDraft.remove ? undefined : (previewLight ?? branding.logoOnLightUrl)
  const previewName = trimmedName || branding.name

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    if (!canSave || !onSaveBranding) return
    await onSaveBranding({
      name: trimmedName,
      logoFile: logoDraft.file,
      removeLogo: logoDraft.remove,
      logoOnLightFile: lightDraft.file,
      removeLogoOnLight: lightDraft.remove,
    })
  }

  // Once the parent reports success, pending file drafts are stored — clear them.
  useEffect(() => {
    if (successMessage) {
      setLogoDraft(emptyDraft)
      setLightDraft(emptyDraft)
      setNameTouched(false)
    }
  }, [successMessage])

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="max-w-container mx-auto space-y-5 px-4 py-6">
        <header className="space-y-1">
          <h1 className="text-xl font-semibold">Settings</h1>
          <p className="text-[13px] text-slate-500">
            Install-wide configuration. Only admins can see this page.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="space-y-6 rounded-lg border border-slate-200 bg-white p-5">
            <div className="max-w-xl space-y-1">
              <h2 className="text-base font-semibold text-slate-900">Branding</h2>
              <p className="text-[13px] text-slate-500">
                The shop name and logos shown in the header, on the login page,
                on the kiosk welcome screen and behind kiosk signatures.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="space-y-6">
                <div className="space-y-2">
                  <FormFieldLabel htmlFor={nameId}>Shop name</FormFieldLabel>
                  <UnderlineInput
                    id={nameId}
                    name="name"
                    value={name}
                    required
                    maxLength={80}
                    disabled={isSaving}
                    onChange={(event) => {
                      setNameTouched(true)
                      setName(event.target.value)
                    }}
                  />
                  {trimmedName.length === 0 ? (
                    <p className="text-[13px] text-rose-600">A shop name is required.</p>
                  ) : null}
                </div>

                <LogoField
                  id={`${idPrefix}-logo`}
                  label="Logo (dark backgrounds)"
                  description="Shown on the kiosk welcome screen. SVG, PNG, WebP or JPEG, up to 1 MB."
                  tone="dark"
                  currentUrl={branding.logoUrl}
                  draft={logoDraft}
                  onChange={setLogoDraft}
                  disabled={isSaving}
                />

                <LogoField
                  id={`${idPrefix}-logo-on-light`}
                  label="Logo (light backgrounds)"
                  description="Optional. Used as the faint watermark behind kiosk signatures; without it the dark-background logo is used."
                  tone="light"
                  currentUrl={branding.logoOnLightUrl}
                  draft={lightDraft}
                  onChange={setLightDraft}
                  disabled={isSaving}
                />
              </div>

              <aside className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Preview
                </h3>
                <div className="rounded-md bg-gray-800 px-4 py-3 text-white">
                  <span className="text-lg font-semibold uppercase leading-none">{previewName}</span>
                  <span className="ml-3 text-xs text-gray-400">header</span>
                </div>
                <div className="rounded-md bg-slate-950 px-4 py-3 text-center text-slate-50">
                  <p className="text-base font-semibold">{loginTitle(previewName)}</p>
                  <span className="text-xs text-slate-400">login page</span>
                </div>
                <div className="flex flex-col items-center rounded-md bg-linear-to-br from-slate-950 via-slate-900 to-slate-800 px-4 py-5 text-center text-white">
                  {effectiveLogo ? (
                    <img src={effectiveLogo} alt="" className="mb-2 h-10 w-auto max-w-[80%]" />
                  ) : (
                    <p className="mb-2 text-xl font-bold uppercase tracking-wide">{previewName}</p>
                  )}
                  <p className="text-sm font-semibold">Welcome</p>
                  <span className="text-xs text-slate-400">kiosk welcome screen</span>
                </div>
                <div className="relative flex h-24 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white">
                  {effectiveLight ?? effectiveLogo ? (
                    <img
                      src={effectiveLight ?? effectiveLogo}
                      alt=""
                      className="h-14 w-auto max-w-[70%] opacity-[0.08]"
                    />
                  ) : null}
                  <span className="absolute bottom-1 right-2 text-xs text-slate-400">
                    signature watermark{effectiveLight ? '' : effectiveLogo ? ' (dark logo reused)' : ' (none)'}
                  </span>
                </div>
              </aside>
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4">
              <button
                type="submit"
                disabled={!canSave}
                className="inline-flex h-9 items-center rounded-lg bg-slate-900 px-4 text-[13px] font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? 'Saving…' : 'Save branding'}
              </button>
              {dirty ? (
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => {
                    setName(branding.name)
                    setNameTouched(false)
                    setLogoDraft(emptyDraft)
                    setLightDraft(emptyDraft)
                  }}
                  className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                >
                  Discard changes
                </button>
              ) : null}
              {errorMessage ? (
                <p className="text-[13px] text-rose-600">{errorMessage}</p>
              ) : successMessage && !dirty ? (
                <p className="text-[13px] text-teal-700">{successMessage}</p>
              ) : null}
            </div>
          </section>
        </form>

        {props.followUpCategoriesSection}

        <TwilioSection
          twilio={props.twilio}
          onSave={props.onSaveTwilio}
          isSaving={props.isSavingTwilio ?? false}
          errorMessage={props.twilioErrorMessage}
          successMessage={props.twilioSuccessMessage}
        />

        {props.whatsappSendingSwitch}

        <MessagingSection
          sendDelaySeconds={props.whatsappSendDelaySeconds}
          onSave={props.onSaveSendDelay}
          isSaving={props.isSavingSendDelay ?? false}
          errorMessage={props.sendDelayErrorMessage}
          successMessage={props.sendDelaySuccessMessage}
        />
      </main>
    </div>
  )
}

export function SectionShell(props: {
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-6 rounded-lg border border-slate-200 bg-white p-5">
      <div className="max-w-xl space-y-1">
        <h2 className="text-base font-semibold text-slate-900">{props.title}</h2>
        <p className="text-[13px] text-slate-500">{props.description}</p>
      </div>
      {props.children}
    </section>
  )
}

export function SectionFooter(props: {
  submitLabel: string
  savingLabel: string
  canSave: boolean
  isSaving: boolean
  dirty: boolean
  onDiscard: () => void
  errorMessage?: string | null
  successMessage?: string | null
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4">
      <button
        type="submit"
        disabled={!props.canSave}
        className="inline-flex h-9 items-center rounded-lg bg-slate-900 px-4 text-[13px] font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {props.isSaving ? props.savingLabel : props.submitLabel}
      </button>
      {props.dirty ? (
        <button
          type="button"
          disabled={props.isSaving}
          onClick={props.onDiscard}
          className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-50"
        >
          Discard changes
        </button>
      ) : null}
      {props.errorMessage ? (
        <p className="text-[13px] text-rose-600">{props.errorMessage}</p>
      ) : props.successMessage && !props.dirty ? (
        <p className="text-[13px] text-teal-700">{props.successMessage}</p>
      ) : null}
    </div>
  )
}

/**
 * The Twilio connection used for WhatsApp sending. The auth token is
 * write-only: the server only reports whether one is stored, and saving with
 * the field left blank keeps it as is.
 */
function TwilioSection(props: {
  twilio?: SettingsTwilio | null
  onSave?: (values: SettingsTwilioValues) => Promise<void> | void
  isSaving: boolean
  errorMessage?: string | null
  successMessage?: string | null
}) {
  const { twilio, onSave, isSaving } = props
  const idPrefix = useId()
  const [draft, setDraft] = useState<SettingsTwilioValues | null>(null)
  const [clearToken, setClearToken] = useState(false)

  // Clear the pending edits (incl. the typed token) once a save lands.
  useEffect(() => {
    if (props.successMessage) {
      setDraft(null)
      setClearToken(false)
    }
  }, [props.successMessage])

  const description =
    'The Twilio account used to send WhatsApp messages. The auth token is stored server-side and never shown again.'

  if (twilio === undefined) {
    return (
      <SectionShell title="Twilio connection" description={description}>
        <p className="text-[13px] text-slate-500">Loading…</p>
      </SectionShell>
    )
  }
  if (twilio === null) {
    return (
      <SectionShell title="Twilio connection" description={description}>
        <p className="text-[13px] text-rose-600">
          Could not load the Twilio settings. Reload the page to try again.
        </p>
      </SectionShell>
    )
  }

  const values: SettingsTwilioValues = draft ?? {
    accountSid: twilio.accountSid,
    fromNumber: twilio.fromNumber,
    messagingServiceSid: twilio.messagingServiceSid,
    authToken: '',
  }
  const dirty =
    draft !== null &&
    (values.accountSid.trim() !== twilio.accountSid ||
      values.fromNumber.trim() !== twilio.fromNumber ||
      values.messagingServiceSid.trim() !== twilio.messagingServiceSid ||
      Boolean(values.authToken?.trim()) ||
      clearToken)
  const canSave = dirty && !isSaving

  const setField = (field: keyof SettingsTwilioValues) =>
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setDraft({ ...values, [field]: event.target.value })

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    if (!canSave || !onSave) return
    await onSave({
      accountSid: values.accountSid.trim(),
      fromNumber: values.fromNumber.trim(),
      messagingServiceSid: values.messagingServiceSid.trim(),
      authToken: values.authToken?.trim() || undefined,
      clearAuthToken: clearToken && !values.authToken?.trim(),
    })
  }

  const tokenPlaceholder = twilio.hasAuthToken
    ? clearToken
      ? 'Will be cleared on save'
      : '•••••••• (saved — leave blank to keep)'
    : 'Not set'

  return (
    <SectionShell title="Twilio connection" description={description}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2">
          <div className="space-y-2">
            <FormFieldLabel htmlFor={`${idPrefix}-sid`}>Account SID</FormFieldLabel>
            <UnderlineInput
              id={`${idPrefix}-sid`}
              value={values.accountSid}
              placeholder="AC…"
              disabled={isSaving}
              onChange={setField('accountSid')}
            />
          </div>
          <div className="space-y-2">
            <FormFieldLabel htmlFor={`${idPrefix}-token`}>Auth token</FormFieldLabel>
            <UnderlineInput
              id={`${idPrefix}-token`}
              type="password"
              autoComplete="new-password"
              value={values.authToken ?? ''}
              placeholder={tokenPlaceholder}
              disabled={isSaving}
              onChange={(event) => {
                setClearToken(false)
                setDraft({ ...values, authToken: event.target.value })
              }}
            />
            {twilio.hasAuthToken && !values.authToken ? (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => {
                  setClearToken((prev) => !prev)
                  setDraft({ ...values })
                }}
                className="text-[12px] font-medium text-slate-500 underline-offset-2 hover:underline"
              >
                {clearToken ? 'Keep the stored token' : 'Clear the stored token'}
              </button>
            ) : null}
          </div>
          <div className="space-y-2">
            <FormFieldLabel htmlFor={`${idPrefix}-from`}>From number</FormFieldLabel>
            <UnderlineInput
              id={`${idPrefix}-from`}
              value={values.fromNumber}
              placeholder="whatsapp:+27…"
              disabled={isSaving}
              onChange={setField('fromNumber')}
            />
          </div>
          <div className="space-y-2">
            <FormFieldLabel htmlFor={`${idPrefix}-mss`}>
              Messaging service SID (optional)
            </FormFieldLabel>
            <UnderlineInput
              id={`${idPrefix}-mss`}
              value={values.messagingServiceSid}
              placeholder="MG…"
              disabled={isSaving}
              onChange={setField('messagingServiceSid')}
            />
          </div>
        </div>
        <p className="text-[13px] text-slate-500">
          Use “Refresh from Twilio” in the{' '}
          <span className="font-medium">Message templates</span> section below
          to check the connection.
        </p>
        <SectionFooter
          submitLabel="Save Twilio settings"
          savingLabel="Saving…"
          canSave={canSave}
          isSaving={isSaving}
          dirty={dirty}
          onDiscard={() => {
            setDraft(null)
            setClearToken(false)
          }}
          errorMessage={props.errorMessage}
          successMessage={props.successMessage}
        />
      </form>
    </SectionShell>
  )
}

/** Operational WhatsApp messaging settings from the app_settings singleton. */
function MessagingSection(props: {
  sendDelaySeconds?: number
  onSave?: (seconds: number) => Promise<void> | void
  isSaving: boolean
  errorMessage?: string | null
  successMessage?: string | null
}) {
  const { sendDelaySeconds, onSave, isSaving } = props
  const idPrefix = useId()
  const [draft, setDraft] = useState<string | null>(null)

  useEffect(() => {
    if (props.successMessage) setDraft(null)
  }, [props.successMessage])

  const description =
    'How long a queued WhatsApp message waits — and can still be cancelled — before it may be sent.'

  if (sendDelaySeconds === undefined) {
    return (
      <SectionShell title="WhatsApp messaging" description={description}>
        <p className="text-[13px] text-slate-500">Loading…</p>
      </SectionShell>
    )
  }

  const value = draft ?? String(sendDelaySeconds)
  const parsed = Number(value)
  const valid = value.trim() !== '' && Number.isInteger(parsed) && parsed >= 0
  const dirty = draft !== null && value.trim() !== String(sendDelaySeconds)
  const canSave = dirty && valid && !isSaving

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    if (!canSave || !onSave) return
    await onSave(parsed)
  }

  return (
    <SectionShell title="WhatsApp messaging" description={description}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="max-w-xs space-y-2">
          <FormFieldLabel htmlFor={`${idPrefix}-delay`}>
            Send delay (seconds)
          </FormFieldLabel>
          <UnderlineInput
            id={`${idPrefix}-delay`}
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={value}
            disabled={isSaving}
            onChange={(event) => setDraft(event.target.value)}
          />
          {!valid ? (
            <p className="text-[13px] text-rose-600">
              Enter a whole number of seconds (0 or more).
            </p>
          ) : null}
        </div>
        <SectionFooter
          submitLabel="Save messaging settings"
          savingLabel="Saving…"
          canSave={canSave}
          isSaving={isSaving}
          dirty={dirty}
          onDiscard={() => setDraft(null)}
          errorMessage={props.errorMessage}
          successMessage={props.successMessage}
        />
      </form>
    </SectionShell>
  )
}
